import { DataBlockDictionaryDto, QueryFactory } from '@lmt/shared-types';
import { DataType } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useAddDictionaryHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useAddDictionaryHandler';
import {
  DataBlockFieldFormData,
  onlyActiveDictionaries,
  useDataTypesList,
  ValueDictionaryColumn,
} from '@lmt/unicorn-shared/app/components/measurement-fields/common';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';

import styles from './CreateDataBlockInputTable.module.scss';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { CheckBox } from '../../common/ui-kit/CheckBox';
import { useDictionaryById } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { localizeDictionaryColumnTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';

type Props = {
  form: UseFormReturn<DataBlockFieldFormData>;
  mode: 'create' | 'edit';
  disabled?: boolean;
};
export const CreateDataBlockInputTable = (props: Props) => {
  const form = props.form;
  const i18n = useScopedTranslation('data_blocks');

  const columns = useFieldArray({
    control: form.control,
    name: 'tableColumns',
    keyName: '_id',
    shouldUnregister: true,
  });
  const removeColumn = useCallback((index: number) => {
    columns.remove(index);
  }, []);

  const addColumn = useCallback(() => {
    columns.append([
      {
        isOptionAllowed: true,
        id: null,
        name: '',
        dataType: null as any,
        dictionaryColumnId: null,
        dictionaryId: null,
        isSingleValue: false,
      },
    ]);
  }, []);
  useEffect(() => {
    if (!form.getValues().tableColumns?.length) addColumn();
  }, []);
  return (
    <div
      className={styles.tableColumnsWrapper}
      data-test-id="create-table-field-columns"
    >
      {columns.fields.map((column, index) => (
        <div key={column._id}>
          <TableColumn
            form={form}
            index={index}
            key={column._id}
            disabled={props.disabled}
          />
          {columns.fields.length > 1 ? (
            <Button
              type={'button'}
              color={ButtonColor.Danger}
              onClick={() => {
                removeColumn(index);
              }}
              disabled={props.disabled}
              title={i18n.t('remove_column_button')}
              className={globalFormStyles.field}
            ></Button>
          ) : null}
        </div>
      ))}
      <div>
        <Button
          type={'button'}
          color={ButtonColor.Primary}
          disabled={props.disabled}
          onClick={() => {
            addColumn();
          }}
          title={i18n.t('add_column_button')}
        ></Button>
      </div>
    </div>
  );
};

type TableColumnProps = {
  index: number;
  form: UseFormReturn<DataBlockFieldFormData>;
  disabled?: boolean;
};
const TableColumn: React.FC<TableColumnProps> = (props) => {
  const { index, form } = props;
  const i18nextGlobal = useTranslation();
  const i18nextDataBlockFields = useTranslation(NS_DataBlockFields);

  const fieldType = form.watch(`tableColumns.${index}.dataType`) as DataType;
  const dataTypes = useDataTypesList({
    usage: 'dataBlockTableColumn',
  });

  const dictionariesQuery = QueryFactory.DataBlockDictionaryQuery.useGetQuery({
    select: onlyActiveDictionaries,
  });
  const dictionaryDropDownOptions = useMemo(
    () =>
      dictionariesQuery.data
        ?.map((x) => ({
          title: i18nextDataBlockFields.t(x.title as any),
          id: x.id,
        }))
        ?.sort((a, b) => a.title.localeCompare(b.title)) ?? [],
    [dictionariesQuery],
  );

  const selectedDictionaryId = form.watch(`tableColumns.${index}.dictionaryId`);
  const selectedDictionary = useDictionaryById(selectedDictionaryId);
  const selectedDictionaryColumns = useMemo(() => {
    if (!selectedDictionary || !dictionariesQuery.data) return undefined;

    if (!selectedDictionary?.columns) return undefined;
    return [
      {
        id: ValueDictionaryColumn,
        title: i18nextGlobal.t('data_blocks.fields.dictionaryColumnValue'),
      },
      ...selectedDictionary?.columns,
    ];
  }, [selectedDictionary, dictionariesQuery.data]);

  const { enumFieldLinkProps, isCreatingNewDictionary } =
    useAddDictionaryHandler(
      useCallback((newDictionary: DataBlockDictionaryDto) => {
        form.setValue(
          `tableColumns.${index}.dictionaryId`,
          newDictionary.id,
          validateAndDirty,
        );
      }, []),
    );

  return (
    <>
      <input type="hidden" {...form.register(`tableColumns.${index}.id`)} />
      <Field title={i18nextGlobal.t('data_blocks.fields.name')}>
        <Input
          {...form.register(`tableColumns.${index}.name`, requiredRule())}
          errorText={form.formState.errors.tableColumns?.[index]?.name?.message}
          autoComplete="off"
          disabled={props.disabled}
        />
      </Field>
      <Field
        title={i18nextGlobal.t('data_blocks.fields.type')}
        className={globalFormStyles.field}
      >
        <HookFormDropDownInput
          enableSearch={true}
          control={form.control}
          defaultValue={undefined}
          rules={requiredRule()}
          name={`tableColumns.${index}.dataType`}
          required
          variant="formInput"
          getOptionLabel="displayValue"
          idFunction="value"
          options={dataTypes}
          errorText={
            form.formState.errors.tableColumns?.[index]?.dataType?.message
          }
          disabled={props.disabled}
        />
      </Field>
      {fieldType === DataType.Boolean && (
        <>
          <Field title="">
            <CheckBox
              title={i18nextGlobal.t('data_blocks.fields.is_single_value')}
              {...form.register(`tableColumns.${index}.isSingleValue`)}
            />
          </Field>
        </>
      )}
      {fieldType === DataType.Dictionary && (
        <>
          <Field
            title={i18nextGlobal.t('data_blocks.fields.dictionaryId')}
            className={globalFormStyles.field}
            linkProps={enumFieldLinkProps}
          >
            <HookFormDropDownInput
              enableSearch={true}
              control={form.control}
              rules={requiredRule()}
              defaultValue={undefined}
              name={`tableColumns.${index}.dictionaryId`}
              required
              variant="formInput"
              getOptionLabel="title"
              idFunction="id"
              errorText={
                form.formState.errors.tableColumns?.[index]?.dictionaryId
                  ?.message
              }
              options={dictionaryDropDownOptions}
              disabled={props.disabled}
            />
          </Field>
          {selectedDictionaryColumns &&
            selectedDictionaryColumns.length > 0 && (
              <Field
                title={i18nextGlobal.t('data_blocks.fields.dictionaryColumn')}
                className={globalFormStyles.field}
              >
                <HookFormDropDownInput
                  enableSearch={true}
                  options={selectedDictionaryColumns}
                  control={form.control}
                  rules={requiredRule()}
                  defaultValue={undefined}
                  name={`tableColumns.${index}.dictionaryColumnId`}
                  required
                  variant="formInput"
                  getOptionLabel={(column) =>
                    localizeDictionaryColumnTitle(
                      selectedDictionary,
                      column.title,
                    )
                  }
                  idFunction="id"
                  errorText={
                    form.formState.errors.tableColumns?.[index]
                      ?.dictionaryColumnId?.message
                  }
                  disabled={props.disabled}
                />
              </Field>
            )}
          <Field title="">
            <CheckBox
              title={i18nextGlobal.t('data_blocks.fields.is_option_allowed')}
              {...form.register(`tableColumns.${index}.isOptionAllowed`)}
            />
          </Field>
        </>
      )}
    </>
  );
};
